/**
 * Shared reviewer-note callout — the violet "Reviewer note" block shown
 * under a stage's output. Extracted from ContractStageTabs and
 * StageHistoryRail (where it was duplicated) so the gate notes roll-up
 * can reuse the exact same markup.
 */
import { MessageSquare } from 'lucide-react';
import { fmtDateTime } from '@/lib/datetime';
import { cn } from '@/lib/utils';

interface Props {
  note: string;
  /** Timestamp shown top-right (e.g. approved_at). */
  at?: string | null;
  /** Heading text; defaults to "Reviewer note". */
  label?: string;
  className?: string;
}

export function ReviewerNoteCallout({ note, at, label = 'Reviewer note', className }: Props) {
  return (
    <div
      className={cn(
        'rounded-md border border-violet-200 bg-violet-50 px-3 py-2 dark:border-violet-900 dark:bg-violet-950/30',
        className,
      )}
    >
      <p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-violet-700 dark:text-violet-300">
        <MessageSquare className="h-3 w-3" aria-hidden="true" />
        {label}
        {at && (
          <span className="ml-auto font-mono text-[10px] normal-case text-violet-600/80 dark:text-violet-400/80">
            {fmtDateTime(at)}
          </span>
        )}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-sm text-violet-950 dark:text-violet-100">
        {note}
      </p>
    </div>
  );
}
